import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { DateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, takeUntil } from 'rxjs/operators';
import { UnitService } from '../services/unit.service';
import { OrganizationUnitDto } from '@shared/services/organization-unit.service';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';

export interface ChangeParentDialogData {
  unitId: string;
  unitName: string;
  currentParentId?: string;
  currentParentName?: string;
}

export interface ChangeParentDialogResult {
  newParentUnitId: string;
  isCorrection: boolean;
  reason?: string | null;
  effectiveDate?: string | null; // ISO date string YYYY-MM-DD
}

@Component({
  selector: 'app-change-parent-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatIconModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    DatePickerComponent,
    ModalShellComponent,
  ],
  templateUrl: './change-parent-dialog.component.html',
  styleUrl: './change-parent-dialog.component.scss',
})
export class ChangeParentDialogComponent implements OnDestroy {
  @ViewChild('parentAutoTrigger') parentAutoTrigger!: MatAutocompleteTrigger;

  readonly modalConfig: ModalShellConfig = { title: 'Cambiar unidad superior', size: 'lg', layout: 'form' };
  form: FormGroup;
  filteredParentUnits: OrganizationUnitDto[] = [];
  filteredParentGroups: { levelId: number; levelLabel: string; units: OrganizationUnitDto[] }[] = [];
  selectedParentUnit: OrganizationUnitDto | null = null;
  isSearchingParent = false;
  isSaving = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ChangeParentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeParentDialogData,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<any>,
    private unitService: UnitService,
  ) {
    this.form = this.fb.group({
      isCorrection: [true],
      parentUnitSearch: ['', Validators.required],
      parentUnitId: [null, Validators.required],
      reason: [null],
      effectiveDate: [null],
    });

    this.form.get('isCorrection')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((isCorrection: boolean) => {
        const effectiveDateCtrl = this.form.get('effectiveDate')!;
        if (isCorrection) {
          effectiveDateCtrl.clearValidators();
        } else {
          effectiveDateCtrl.setValidators(Validators.required);
        }
        effectiveDateCtrl.updateValueAndValidity();
      });

    this.form.get('parentUnitSearch')!.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      switchMap(term => {
        if (typeof term !== 'string') return of(null);
        const selectedId = this.form.get('parentUnitId')?.value;
        const selectedName = this.selectedParentUnit?.name;
        if (selectedId && term === selectedName) return of(null);
        if (selectedId) {
          this.form.get('parentUnitId')!.setValue(null, { emitEvent: false });
          this.selectedParentUnit = null;
        }
        if (!term.trim()) {
          this.filteredParentUnits = [];
          return of(null);
        }
        this.isSearchingParent = true;
        return this.unitService.searchUnits(term).pipe(catchError(() => of(null)));
      }),
    ).subscribe(result => {
      this.isSearchingParent = false;
      if (result !== null) {
        // Exclude the unit itself and communities (cannot be a parent of another unit)
        const filtered = (result?.items ?? []).filter(
          u => u.id !== this.data.unitId && u.levelName?.toLowerCase() !== 'comunidad',
        );
        this.filteredParentUnits = filtered;
        this.filteredParentGroups = this.groupByLevel(filtered);
        this.parentAutoTrigger?.openPanel();
      }
    });
  }

  get isCorrection(): boolean {
    return this.form.get('isCorrection')!.value === true;
  }

  onParentSelected(unit: OrganizationUnitDto): void {
    this.selectedParentUnit = unit;
    this.form.get('parentUnitId')!.setValue(unit.id);
    this.form.get('parentUnitSearch')!.setValue(unit.name, { emitEvent: false });
    this.filteredParentUnits = [];
    this.filteredParentGroups = [];
  }

  private groupByLevel(items: OrganizationUnitDto[]): { levelId: number; levelLabel: string; units: OrganizationUnitDto[] }[] {
    const grouped = new Map<number, OrganizationUnitDto[]>();
    for (const unit of items) {
      const levelId = unit.organizationLevelId ?? 0;
      if (!grouped.has(levelId)) grouped.set(levelId, []);
      grouped.get(levelId)!.push(unit);
    }
    return Array.from(grouped.entries())
      .sort(([a], [b]) => a - b)
      .map(([levelId, units]) => ({
        levelId,
        levelLabel: units[0].levelName ?? `Nivel ${levelId}`,
        units,
      }));
  }

  displayUnit(unit: OrganizationUnitDto | string | null): string {
    if (!unit) return '';
    if (typeof unit === 'string') return unit;
    return unit.name;
  }

  onConfirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const effectiveDateRaw = this.form.get('effectiveDate')!.value;
    let effectiveDateIso: string | null = null;
    if (effectiveDateRaw) {
      effectiveDateIso = this.toIsoDate(effectiveDateRaw);
    }

    const result: ChangeParentDialogResult = {
      newParentUnitId: this.form.get('parentUnitId')!.value,
      isCorrection: this.form.get('isCorrection')!.value,
      reason: this.form.get('reason')!.value || null,
      effectiveDate: effectiveDateIso,
    };

    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
}
